// src/context/CurrencyContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export const CURRENCIES = [
  { code: 'INR', symbol: '₹',  label: 'INR', flag: '🇮🇳' },
  { code: 'USD', symbol: '$',  label: 'USD', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€',  label: 'EUR', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£',  label: 'GBP', flag: '🇬🇧' },
  { code: 'AED', symbol: 'د.إ',label: 'AED', flag: '🇦🇪' },
  { code: 'SAR', symbol: '﷼',  label: 'SAR', flag: '🇸🇦' },
  { code: 'SGD', symbol: 'S$', label: 'SGD', flag: '🇸🇬' },
  { code: 'CAD', symbol: 'C$', label: 'CAD', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', label: 'AUD', flag: '🇦🇺' },
];

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(
    () => localStorage.getItem('aamira_currency') || 'INR'
  );
  const [rates, setRates]     = useState({ INR: 1 });
  const [loading, setLoading] = useState(false);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
      const data = await res.json();
      if (data.rates) setRates(data.rates);
    } catch {
      // Fallback hardcoded approximate rates if API fails
      setRates({
        INR: 1,
        USD: 0.012,
        EUR: 0.011,
        GBP: 0.0094,
        AED: 0.044,
        SAR: 0.045,
        SGD: 0.016,
        CAD: 0.016,
        AUD: 0.018,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch rates once on mount
  useEffect(() => { fetchRates(); }, [fetchRates]);

  const setCurrency = (code) => {
    setCurrencyState(code);
    localStorage.setItem('aamira_currency', code);
  };

  // Convert an INR price to selected currency
  const convert = useCallback((inrAmount) => {
    if (!inrAmount) return 0;
    const rate = rates[currency] || 1;
    return inrAmount * rate;
  }, [rates, currency]);

  // Format a converted price with symbol
  const formatPrice = useCallback((inrAmount) => {
    const converted = convert(inrAmount);
    const curr      = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

    if (currency === 'INR') {
      return `₹${Number(converted).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    }
    return `${curr.symbol}${Number(converted).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convert, currency]);

  const activeCurrency = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, formatPrice, loading, activeCurrency, CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
};